import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../AuthProvider';
import { getApiToken } from '../authUtils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    CircularProgress,
} from '@mui/material';
import './SmartProductDetail.css';

const urlconfig = require('../config/urlconfig');

/* Last 5 purchases of this product, grouped the way smart search groups cards: by the card's
   cost sheet line for internal history, or by ESIN for a catalog-only product */
const PURCHASE_HISTORY_API = (subItemId, esin) => {
    const params = new URLSearchParams();
    if (subItemId != null) params.set('subItemId', subItemId);
    else if (esin) params.set('esin', esin);
    return `${urlconfig.backend_api_base_url}/api/v2/smart-search/purchase-history?${params.toString()}`;
};
const COST_SHEET_API = (id) => `${urlconfig.backend_api_base_url}/api/v2/items/${id}`;
const VENDOR_API = (id) => `${urlconfig.backend_api_base_url}/api/v2/vendors/${id}`;

const formatCurrency = (amount) => {
    if (amount == null || amount === '' || isNaN(Number(amount))) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
};

const formatDate = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    return isNaN(date) ? d : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const norm = (v) => (v == null ? '' : String(v).trim().toLowerCase());

/* A cost sheet line is this product when its ESIN matches, or for lines without one, its details */
const isSameProduct = (sub, esin, details) =>
    (esin && norm(sub.esin) === norm(esin)) || (norm(details) && norm(sub.details) === norm(details));

/* =============================================================== */
const CostSheetDialog = ({ open, onClose, soNumber, sheet, subItemId, loading, vendors, product, esin }) => {
    const { matchedItems, totalItems } = useMemo(() => {
        const all = sheet?.items || [];
        return {
            // The clicked purchase's own line, plus any other line of the same product in this sheet
            matchedItems: all.filter((sub) => sub.id === subItemId || isSameProduct(sub, esin, product.description)),
            totalItems: all.length,
        };
    }, [sheet, subItemId, product, esin]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ className: 'cs-dialog', sx: { borderRadius: 3 } }}>
            <DialogTitle className="cs-dialog-title">
                <div>
                    <span className="cs-title-label">Cost Sheet</span>
                    <span className="cs-title-so">SO #{soNumber}</span>
                </div>
                <IconButton onClick={onClose} size="small" aria-label="close">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {loading && <div className="cs-state"><CircularProgress size={24} /></div>}
                {!loading && !sheet && <div className="cs-state">Could not load this cost sheet.</div>}
                {!loading && sheet && (
                    <>
                        {/* Header info */}
                        <div className="cs-meta">
                            <div className="data-point">
                                {/* Same field the cost sheet form labels by costsheetfor (UserInput.js) */}
                                <span className="label">{sheet.costsheetfor === 'Sample' ? 'Sample Date' : 'SO Date'}</span>
                                <span className="value">{formatDate(sheet.date)}</span>
                            </div>
                            <div className="data-point">
                                <span className="label">Customer</span>
                                <span className="value">{sheet.clientName || '-'}</span>
                            </div>
                            <div className="data-point">
                                <span className="label">Items in SO</span>
                                <span className="value">{totalItems}</span>
                            </div>
                        </div>

                        {totalItems > 1 && matchedItems.length > 0 && (
                            <div className="cs-filter-note">
                                Showing only <strong>{product.productName}</strong> ({matchedItems.length} of {totalItems} line items)
                            </div>
                        )}

                        {matchedItems.length === 0 ? (
                            <div className="cs-state">This item was not found in SO #{soNumber}.</div>
                        ) : (
                            <div className="cs-table-wrapper">
                                <table className="cs-table">
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Vendor</th>
                                            <th className="num">Qty</th>
                                            <th className="num">Purchase Price</th>
                                            <th className="num">Selling Price</th>
                                            <th className="num">Margin</th>
                                            <th className="num">Total Cost</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {matchedItems.map((sub) => (
                                            <tr key={sub.id}>
                                                <td>
                                                    <div className="cs-item-name">{product.productName || sub.details}</div>
                                                    {sub.esin && <div className="cs-item-sub">ESIN: {sub.esin}</div>}
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const vendor = vendors[sub.legalVendorId];
                                                        return (
                                                            <>
                                                                <div className="cs-item-name">{vendor?.legalVendorName || sub.vendorName || '-'}</div>
                                                                {vendor?.vendorGST && <div className="cs-item-sub">GST: {vendor.vendorGST}</div>}
                                                                {vendor?.vendorSpoc && (
                                                                    <div className="cs-item-sub">
                                                                        {vendor.vendorSpoc}{vendor.vendorSpocNumber ? ` · ${vendor.vendorSpocNumber}` : ''}
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="num">{sub.qty ?? 0}</td>
                                                <td className="num">{formatCurrency(sub.pp)}</td>
                                                <td className="num">{formatCurrency(sub.sp)}</td>
                                                <td className="num">
                                                    {sub.marginPercent != null ? `${Number(sub.marginPercent).toFixed(2)}%` : '-'}
                                                </td>
                                                <td className="num strong">{formatCurrency(sub.totalPp)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <button type="button" className="cs-close-btn" onClick={onClose}>Close</button>
            </DialogActions>
        </Dialog>
    );
};

/* =============================================================== */
const SmartProductDetails = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const product = state?.product;
    const { account } = useAuth();
    const { instance } = useMsal();
    // Same product key smart search groups cards by: the ESIN (catalog id) when there is one
    const esin = product?.esin || (product?.ecomProductId != null ? String(product.ecomProductId) : null);
    const subItemId = product?.source === 'OMS' ? product?.subItemId : null;
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(false);
    const [costSheetView, setCostSheetView] = useState({ open: false, soNumber: null, subItemId: null, sheet: null, loading: false });
    const [vendors, setVendors] = useState({});

    useEffect(() => {
        // Start from an empty table, so another product's rows never show while this one loads or
        // if its request fails
        setHistory([]);
        setHistoryError(false);
        if (subItemId == null && !esin) return;
        let cancelled = false;

        (async () => {
            setHistoryLoading(true);
            try {
                const token = await getApiToken(instance, account);
                const res = await fetch(PURCHASE_HISTORY_API(subItemId, esin), {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`Request failed (${res.status})`);
                const rows = await res.json();
                if (!cancelled) setHistory(rows || []);
            } catch (e) {
                console.error('Purchase history lookup failed', e);
                if (!cancelled) setHistoryError(true);
            } finally {
                if (!cancelled) setHistoryLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [subItemId, esin, account, instance]);

    // Only the newest cost sheet request may fill the dialog, so a slow response for an SO clicked
    // earlier can't show its lines under the SO clicked last
    const latestCostSheetRef = useRef(0);
    const openCostSheet = async (row) => {
        const requestId = ++latestCostSheetRef.current;
        const isLatest = () => requestId === latestCostSheetRef.current;
        setCostSheetView({ open: true, soNumber: row.salesOrderNo, subItemId: row.subItemId, sheet: null, loading: true });
        try {
            const token = await getApiToken(instance, account);
            const headers = { Authorization: `Bearer ${token}` };
            const res = await fetch(COST_SHEET_API(row.itemId), { headers });
            if (!res.ok) throw new Error(`Request failed (${res.status})`);
            const sheet = await res.json();
            if (!isLatest()) return;

            // Lines picked from the vendor registry carry only legalVendorId (vendorName is blank),
            // so resolve those the same way LineItemsTable does.
            const vendorIds = [...new Set(
                (sheet.items || [])
                    .filter((sub) => (sub.id === row.subItemId || isSameProduct(sub, esin, product.description)) && sub.legalVendorId != null)
                    .map((sub) => sub.legalVendorId)
                    .filter((id) => !vendors[id])
            )];
            const entries = await Promise.all(vendorIds.map(async (id) => {
                try {
                    const vRes = await fetch(VENDOR_API(id), { headers });
                    return vRes.ok ? [id, await vRes.json()] : null;
                } catch {
                    return null;
                }
            }));
            setVendors((prev) => ({ ...prev, ...Object.fromEntries(entries.filter(Boolean)) }));
            if (isLatest()) setCostSheetView((prev) => ({ ...prev, sheet, loading: false }));
        } catch (e) {
            console.error('Cost sheet lookup failed', e);
            if (isLatest()) setCostSheetView((prev) => ({ ...prev, loading: false }));
        }
    };

    if (!product) return <div className="loading-state">Product not found. Please search again.</div>;

    const imageUrl = product.ecomThumbnailUrl || 'https://via.placeholder.com/400?text=No+Image';
    const displayEsin = product.ecomProductId ? product.ecomProductId : 'N/A';
    // Smart search can return no vendor if the registry lookup failed; fall back to the history
    const lastVendorName = product.lastVendor || history[0]?.vendorName;

    return (
        <div className="smart-detail-page">
            <button type="button" className="sd-back" onClick={() => navigate(-1)}>
                <ArrowBackIcon fontSize="small" /> Back to search
            </button>

            <div className="sd-layout">
                {/* --- LEFT: image --- */}
                <aside className="sd-media">
                    <div className="sd-media-frame">
                        <img src={imageUrl} alt={product.productName} className="sd-media-img" />
                    </div>
                </aside>

                {/* --- RIGHT: identity + data --- */}
                <div className="sd-main">
                    <header className="sd-identity">
                        <div className="sd-tags">
                            <span className="sd-tag sd-tag--category">{product.omsCategory || 'Uncategorized'}</span>
                            <span className="sd-tag sd-tag--mono">ESIN {displayEsin}</span>
                        </div>
                        <h1 className="sd-title">{product.productName}</h1>
                        <p className="sd-brand">Brand <strong>{product.brandName || 'Generic'}</strong></p>
                    </header>

                    {/* Internal history */}
                    <section className="sd-panel sd-panel--oms">
                        <div className="sd-panel-head">
                            <h2>Internal History</h2>
                            <span className="sd-panel-tag">OMS data</span>
                        </div>

                        <div className="sd-stats">
                            <div className="sd-stat">
                                <span className="sd-stat-label">Last vendor</span>
                                <span className="sd-stat-value sd-stat-value--text" title={lastVendorName || undefined}>
                                    {lastVendorName || '-'}
                                </span>
                            </div>
                            <div className="sd-stat">
                                <span className="sd-stat-label">Last purchase price</span>
                                <span className="sd-stat-value sd-stat-value--accent">
                                    {formatCurrency(product.lastPurchasePrice ?? history[0]?.purchasePrice)}
                                </span>
                            </div>
                            <div className="sd-stat">
                                <span className="sd-stat-label">Times purchased</span>
                                <span className="sd-stat-value">
                                    {product.purchaseCount != null ? product.purchaseCount : '-'}
                                </span>
                            </div>
                        </div>

                        <h3 className="sd-subhead">Last 5 purchases</h3>
                        {historyLoading ? (
                            <div className="sd-state"><CircularProgress size={20} /></div>
                        ) : historyError ? (
                            <div className="sd-state sd-state--error">Couldn't load the purchase history. Please try again later.</div>
                        ) : history.length === 0 ? (
                            <div className="sd-state">No purchases recorded for this product.</div>
                        ) : (
                            <div className="sd-table-wrap">
                                <table className="sd-table">
                                    <thead>
                                        <tr>
                                            <th>Vendor</th>
                                            <th className="num">Purchase Price (Excl. GST)</th>
                                            <th>SO Number</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((row) => (
                                            <tr key={row.subItemId}>
                                                <td className="sd-vendor">{row.vendorName || '-'}</td>
                                                <td className="num">{formatCurrency(row.purchasePrice)}</td>
                                                <td>
                                                    {row.salesOrderNo ? (
                                                        <button
                                                            type="button"
                                                            className="so-chip-btn"
                                                            onClick={() => openCostSheet(row)}
                                                            title="View cost sheet"
                                                        >
                                                            <ReceiptLongIcon fontSize="inherit" />
                                                            {row.salesOrderNo}
                                                        </button>
                                                    ) : <span className="sd-muted">-</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    {/* Catalog data */}
                    <section className="sd-panel sd-panel--market">
                        <div className="sd-panel-head">
                            <h2>Market Specifications</h2>
                            <span className="sd-panel-tag">Catalog data</span>
                        </div>

                        <div className="sd-stats sd-stats--2">
                            <div className="sd-stat">
                                <span className="sd-stat-label">Market MRP</span>
                                <span className="sd-stat-value">{formatCurrency(product.ecomMrp)}</span>
                            </div>
                            <div className="sd-stat">
                                <span className="sd-stat-label">Stock availability</span>
                                <span className={`sd-stock ${product.ecomStock > 0 ? 'sd-stock--in' : 'sd-stock--out'}`}>
                                    {product.ecomStock > 0 ? `${product.ecomStock} units` : 'Out of stock'}
                                </span>
                            </div>
                        </div>

                        <h3 className="sd-subhead">Description</h3>
                        <p className="sd-description">
                            {product.ecomDescription || 'No additional description available.'}
                        </p>
                    </section>
                </div>
            </div>

            <CostSheetDialog
                open={costSheetView.open}
                onClose={() => setCostSheetView((prev) => ({ ...prev, open: false }))}
                soNumber={costSheetView.soNumber}
                sheet={costSheetView.sheet}
                subItemId={costSheetView.subItemId}
                loading={costSheetView.loading}
                vendors={vendors}
                product={product}
                esin={esin}
            />
        </div>
    );
};

export default SmartProductDetails;